<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useWindowHeight } from '@spectee/composables/useWindowHeight';
import TextArea from '@spectee/components/Atoms/Textarea/index.vue';
import Textfield from '@spectee/components/Atoms/TextField/index.vue';
import Heading from '@spectee/components/Atoms/Heading/index.vue';
import Button from '@spectee/components/Atoms/Button/index.vue';
import ScrollBox from '@spectee/components/Atoms/ScrollBox/index.vue';
import Card from '@spectee/components/Atoms/Card/index.vue';
import Modal from '@spectee/components/Organisms/Modal/index.vue';
import Icon from '@spectee/components/Atoms/IconBase/index.vue';
import InquirySendCompleted from '@spectee/components/Organisms/InquirySendCompleted/index.vue';
import TypeLabel from '@spectee/components/Atoms/TypeLabel/index.vue';
import { useSaveInquiry } from '@spectee/graphql/mutations/saveInquiry';
import { phoneNumberValidate, mailValidate } from '@spectee/ts/validation';
import { commonMessage } from '@spectee/const/messageContents';
import { useOverlayLoading } from '@spectee/composables/useOverlayLoading';
import messages from '../../../../messages.json';

// Set the initial page height based on the window height
const { pageHeight } = useWindowHeight();

const from = (window.history.state as any)?.from;
const subject =
  from === 'LicenseManagement' ? messages.inquiry.increaseLicenseLimit : '';

interface InquiryFieldType {
  name: string;
  companyName: string;
  email: string;
  tel: string;
  subject: string;
  content: string;
}

type InquiryFieldKey = keyof InquiryFieldType;

interface FieldErrorType {
  isError?: boolean;
  message?: string;
}

type InquiryErrorType = Record<InquiryFieldKey, FieldErrorType>;

const keyMap: Record<InquiryFieldKey, string> = {
  name: messages.inquiry.name,
  companyName: messages.inquiry.companyName,
  email: messages.inquiry.email,
  tel: messages.inquiry.tel,
  subject: messages.inquiry.subject,
  content: messages.inquiry.content,
};

const inquiryData = ref<InquiryFieldType>({
  name: '',
  companyName: '',
  email: '',
  tel: '',
  subject: subject,
  content: '',
});

const inquiryError = ref<InquiryErrorType>({
  name: {},
  companyName: {},
  email: {},
  tel: {},
  subject: {},
  content: {},
});

const isSubmitError = ref(false);
const displayInquiryForm = ref(true);
const modalSize = ref('small');

const onBlur = (event: FocusEvent) => {
  const { name, value } = event.target as HTMLInputElement;
  const fieldName = name as InquiryFieldKey;

  inquiryError.value[fieldName] = {};
  if (value === '') {
    inquiryError.value[fieldName] = {
      isError: true,
      message: commonMessage.requiredField(keyMap[fieldName]),
    };
    return;
  }
  if (fieldName == 'tel' && !phoneNumberValidate(value)) {
    inquiryError.value[fieldName] = {
      isError: true,
      message: commonMessage.phoneNumberError,
    };
  } else if (fieldName == 'email' && !mailValidate(value)) {
    inquiryError.value[fieldName] = {
      isError: true,
      message: commonMessage.emailError,
    };
  }
};

const isError = () => {
  if (isErrorExist()) return true;
  (Object.entries(inquiryData.value) as [InquiryFieldKey, string][])
    .filter(([key, value]) => key !== 'content' && isEmpty(value))
    .forEach(([key]) => {
      inquiryError.value[key] = {
        isError: true,
        message: commonMessage.requiredField(keyMap[key]),
      };
    });
  return isErrorExist();
};

const isErrorExist = () => {
  let firstErrorKey = findErrorKey();
  if (firstErrorKey) {
    document.getElementById(firstErrorKey)?.focus();
    return true;
  }
  return false;
};

const findErrorKey = () => {
  return (
    Object.entries(inquiryError.value) as [InquiryFieldKey, FieldErrorType][]
  ).find(([, fieldError]) => fieldError.isError)?.[0];
};

const isEmpty = (value: string) => {
  return value === '' || value === undefined || value === null;
};

const { mutate: saveInquiry, loading: saveLoading } = useSaveInquiry();
useOverlayLoading('saveLoading', saveLoading);

const submitForm = async () => {
  if (isError()) return;
  try {
    const res = await saveInquiry({
      input: inquiryData.value,
    });
    if (res && !Object.prototype.hasOwnProperty.call(res, 'errors')) {
      displayInquiryForm.value = false;
    } else {
      isSubmitError.value = true;
    }
  } catch (error) {
    isSubmitError.value = true;
  }
};

const closeErrorModal = () => {
  isSubmitError.value = false;
};

const updateModalSize = () => {
  modalSize.value = window.innerWidth <= 425 ? 'extraSmall' : 'small';
};

onMounted(() => {
  updateModalSize();
  window.addEventListener('resize', updateModalSize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateModalSize);
});
</script>

<template>
  <section class="Inquiry" :style="{ height: pageHeight }">
    <main class="Inquiry__main">
      <ScrollBox class="Inquiry__contentdiv">
        <div class="Inquiry__contents">
          <Heading
            :level="3"
            type="secondary"
            :heading="messages.inquiry.title"
            class="Inquiry__heading"
          />
          <Card class="Inquiry__card" v-if="displayInquiryForm">
            <div class="Inquiry__details">
              <div>
                <Heading :level="3" :heading="keyMap.name" type="quaternary" />
                <Textfield
                  id="name"
                  name="name"
                  :value="inquiryData.name"
                  :placeholder="keyMap.name"
                  :isError="inquiryError.name.isError"
                  :errorText="inquiryError.name.message"
                  @input="inquiryData.name = $event.target.value"
                  @blur="onBlur($event)"
                />
              </div>

              <div>
                <Heading
                  :level="3"
                  :heading="keyMap.companyName"
                  type="quaternary"
                />
                <Textfield
                  id="companyName"
                  name="companyName"
                  :placeholder="keyMap.companyName"
                  :value="inquiryData.companyName"
                  :isError="inquiryError.companyName.isError"
                  :errorText="inquiryError.companyName.message"
                  @input="inquiryData.companyName = $event.target.value"
                  @blur="onBlur($event)"
                />
              </div>

              <div>
                <Heading :level="3" :heading="keyMap.email" type="quaternary" />
                <Textfield
                  id="email"
                  name="email"
                  :placeholder="keyMap.email"
                  :value="inquiryData.email"
                  :isError="inquiryError.email.isError"
                  :errorText="inquiryError.email.message"
                  @input="inquiryData.email = $event.target.value"
                  @blur="onBlur($event)"
                />
              </div>

              <div>
                <Heading :level="3" :heading="keyMap.tel" type="quaternary" />
                <Textfield
                  id="tel"
                  name="tel"
                  :placeholder="keyMap.tel"
                  :value="inquiryData.tel"
                  :isError="inquiryError.tel.isError"
                  :errorText="inquiryError.tel.message"
                  @input="inquiryData.tel = $event.target.value"
                  @blur="onBlur($event)"
                />
              </div>

              <div>
                <Heading
                  :level="3"
                  :heading="keyMap.subject"
                  type="quaternary"
                />
                <Textfield
                  id="subject"
                  name="subject"
                  :placeholder="keyMap.subject"
                  :value="inquiryData.subject"
                  :isError="inquiryError.subject.isError"
                  :errorText="inquiryError.subject.message"
                  @input="inquiryData.subject = $event.target.value"
                  @blur="onBlur($event)"
                />
              </div>

              <div>
                <div class="Inquiry__optionalDiv">
                  <Heading
                    :level="3"
                    :heading="keyMap.content"
                    type="quaternary"
                  />
                  <TypeLabel :isRequired="false" />
                </div>
                <TextArea
                  id="content"
                  name="content"
                  :rows="10"
                  :placeholder="messages.inquiry.content"
                  :value="inquiryData.content"
                  @inputVal="inquiryData.content = $event"
                />
              </div>

              <div class="Inquiry__buttondiv">
                <Button
                  tag="button"
                  color="blue"
                  :label="messages.common.send"
                  @click="submitForm"
                ></Button>
              </div>
              <Modal
                v-if="isSubmitError"
                ref="modal"
                class="Inquiry__modal"
                :heading="messages.inquiry.submitInquiry"
                :size="modalSize"
                @closeModal="closeErrorModal"
              >
                <Icon class="Inquiry__icon" iconName="Exception"></Icon>
                <!-- safe usage of v-html: content is fully static and developer-controlled -->
                <p
                  class="Inquiry__modalText"
                  v-html="messages.inquiry.fail.inquirySend"
                ></p>
                <div class="Inquiry__modalButton">
                  <Button
                    :label="messages.common.close"
                    dynamics="secondary"
                    tag="button"
                    @click="closeErrorModal"
                  />
                </div>
              </Modal>
            </div>
          </Card>
          <Card v-else>
            <InquirySendCompleted />
          </Card>
        </div>
      </ScrollBox>
    </main>
  </section>
</template>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_forward.scss' as *;

.Inquiry {
  display: flex;
  flex-direction: column;
  margin-bottom: $service-unit * 2;
  overflow: hidden;

  &__main {
    height: 100%;
    overflow: hidden;
  }

  &__heading {
    margin: $service-unit * 4 0;
  }

  &__contents {
    display: flex;
    flex-direction: column;
    padding: 0 $service-unit * 5;
  }

  &__card {
    display: flex;
  }

  &__details {
    position: relative;
    display: flex;
    gap: $service-unit * 6;
    min-width: $service-min-width;
    flex-direction: column;
    width: 40%;
    left: 28%;
    background-color: $color-white-100;
    margin-top: $service-unit * 6;
    margin-bottom: $service-unit * 15;
    padding: $service-unit * 5;
    overflow-x: auto;
  }

  &__contentdiv {
    background-color: $color-gray-20;
    height: 100%;
    align-items: center;
  }

  &__buttondiv {
    height: $service-unit * 10;
    display: inline-block;
    align-self: center;
  }

  &__modalText,
  & &__modalButton {
    margin-top: $service-unit * 6;
  }
  & &__modal {
    text-align: center;
  }

  &__modalText {
    font-size: $service-font-size-pp;
    line-height: $service-line-height-mf;
    text-align: center;
  }

  &__icon {
    width: $service-unit * 10;
    margin: $service-unit * 4 $service-unit * 38 0 $service-unit * 38;
    fill: #c33b2e;
  }
  &__optionalDiv {
    display: flex;
    align-items: center;
    gap: $service-unit;
  }

  @media (max-width: 426px) {
    &__details {
      left: 0;
      padding: $service-unit * 5;
      margin-top: $service-unit * 2;
      width: 100%;
    }
  }

  @media (min-width: 426px) and (max-width: 426px) {
    &__details {
      left: $service-unit * 6;
      padding: $service-unit * 5;
    }
  }

  @media (max-width: 767px) {
    &__details {
      left: 0;
      width: 100%;
      min-width: $service-unit * 62;
    }

    &__main {
      position: relative;
    }

    &__card {
      width: 100%;
      min-width: $service-unit * 62;
    }

    &__icon {
      margin-left: $service-unit * 30;
    }
  }
}
</style>
